import styled from 'styled-components';

export const ReconciliationContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 24px;
  background: ${({ theme }) => theme.colors.gray100};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

export const PageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PageHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ActionMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 220px;
  z-index: 100;
  overflow: hidden;
`;

export const ActionMenuItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.gray100};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  }
`;

export const ActionButtonWrapper = styled.div`
  position: relative;
`;

export const Tooltip = styled.span`
  position: absolute;
  bottom: -60px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.gray900};
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: normal;
  max-width: 280px;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  line-height: 1.4;

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${({ theme }) => theme.colors.gray900};
  }
`;

export const PageDescription = styled.p`
  margin: 4px 0 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const Subtitle = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-left: auto;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

export const FilterBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${({ theme }) => theme.colors.surface};
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  display: grid;
  grid-template-columns: auto 1fr 1fr 1fr auto auto;
  gap: 12px;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: auto 1fr 1fr auto auto;
    gap: 10px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 12px;
    gap: 8px;
  }
`;

export const FilterLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`;

export const FilterCount = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`;

export const SnapshotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

export const SnapshotCard = styled.div<{ $bg: string }>`
  padding: 16px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $bg }) => $bg.replace('f', 'e')};

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

export const SnapshotTitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

export const SnapshotValue = styled.div<{ $color: string }>`
  font-size: 20px;
  font-weight: 700;
  color: ${({ $color }) => $color};

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const MetricsBar = styled.div`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
    font-size: 11px;
  }
`;

export const TableContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  margin-bottom: 24px;

  @media (max-width: 768px) {
    overflow-x: auto;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  @media (max-width: 768px) {
    font-size: 12px;
    min-width: 800px;
  }
`;

export const TableHead = styled.thead`
  background: ${({ theme }) => theme.colors.gray100};
`;

export const TableRow = styled.tr<{ $highlight?: boolean }>`
  background: ${({ $highlight, theme }) =>
    $highlight ? '#fff7ed' : theme.colors.surface};
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray100};
  }
`;

export const TableCell = styled.td`
  padding: 12px;
  text-align: left;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};

  @media (max-width: 768px) {
    padding: 8px;
  }
`;

export const TableHeader = styled.th`
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media (max-width: 768px) {
    padding: 8px;
  }
`;

export const AgentGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

export const AgentCard = styled.div<{ $overduePct: number; $blockedCount: number }>`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 4px solid ${({ $overduePct, $blockedCount }) => {
    // Agent Risk Color Logic:
    // Red: >50% overdue OR has blocked shipments (high risk)
    // Amber: 25-50% overdue (medium risk)
    // Green: <25% overdue (low risk)
    // Powers: agent incentives, commission holds, territory reassignment
    if ($overduePct > 50 || $blockedCount > 0) return '#dc2626'; // red - high risk
    if ($overduePct >= 25) return '#f59e0b'; // amber - medium risk  
    return '#10b981'; // green - low risk
  }};
  border-radius: 8px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

export const AgentName = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const AgentMetric = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

export const ActionLinks = styled.span`
  display: inline-flex;
  gap: 12px;
  margin-left: 8px;

  @media (max-width: 768px) {
    gap: 8px;
    flex-wrap: wrap;
  }
`;

export const ActionLink = styled.a<{ $variant?: 'danger' | 'success' | 'primary' | 'purple' }>`
  color: ${({ $variant }) => {
    switch ($variant) {
      case 'danger': return '#b91c1c';
      case 'success': return '#16a34a';
      case 'primary': return '#1e40af';
      case 'purple': return '#7c3aed';
      default: return '#1e40af';
    }
  }};
  text-decoration: none;
  font-size: 12px;
  
  &:hover {
    text-decoration: underline;
  }
`;

export const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media (max-width: 768px) {
    padding: 32px 16px;
  }
`;

export const RestrictedView = styled.div`
  margin-top: 16px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray100};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 8px;
`;
